import type { AeEvaluatedQuestion, AeEvaluatedSection } from '@/types/assessmentEngine';

export type AePresentationStyle = 'question' | 'section' | 'subsection';

export interface PresentationStep {
  id: string;
  label: string;
  overview?: string | null;
  questions: AeEvaluatedQuestion[];
  /** First question key in step — used for resume. */
  anchorQuestionKey: string;
}

function visibleQuestionsInSection(sec: AeEvaluatedSection): AeEvaluatedQuestion[] {
  const out: AeEvaluatedQuestion[] = [];
  for (const q of sec.questions) {
    if (q.visible) out.push(q);
  }
  for (const sub of sec.subsections) {
    if (sub.visible) out.push(...visibleQuestionsInSection(sub));
  }
  return out;
}

function collectSubsectionSteps(sec: AeEvaluatedSection): PresentationStep[] {
  const steps: PresentationStep[] = [];
  const direct = sec.questions.filter((q) => q.visible);
  const visibleSubs = sec.subsections.filter((s) => s.visible);

  if (visibleSubs.length === 0 && direct.length > 0) {
    steps.push({
      id: sec.id,
      label: sec.name,
      overview: sec.overview,
      questions: direct,
      anchorQuestionKey: direct[0]!.question_key,
    });
    return steps;
  }

  for (const sub of visibleSubs) {
    const qs = visibleQuestionsInSection(sub);
    if (qs.length === 0) continue;
    steps.push({
      id: sub.id,
      label: sub.name,
      overview: sub.overview ?? sec.overview,
      questions: qs,
      anchorQuestionKey: qs[0]!.question_key,
    });
  }

  return steps;
}

/** Build respondent steps for section or subsection presentation style. */
export function buildPresentationSteps(
  style: Exclude<AePresentationStyle, 'question'>,
  sections: AeEvaluatedSection[]
): PresentationStep[] {
  const steps: PresentationStep[] = [];
  for (const sec of sections) {
    if (!sec.visible) continue;
    if (style === 'section') {
      const qs = visibleQuestionsInSection(sec);
      if (qs.length === 0) continue;
      steps.push({
        id: sec.id,
        label: sec.name,
        overview: sec.overview,
        questions: qs,
        anchorQuestionKey: qs[0]!.question_key,
      });
    } else {
      steps.push(...collectSubsectionSteps(sec));
    }
  }
  return steps;
}

/** Map resume question key to step index for section/subsection modes. */
export function stepIndexForQuestionKey(steps: PresentationStep[], questionKey: string): number {
  const idx = steps.findIndex((s) => s.questions.some((q) => q.question_key === questionKey));
  return idx >= 0 ? idx : 0;
}
